import { Track } from "@/src/lib/tracking";
import { proxy } from "valtio";
import { subscribeKey } from "valtio/utils";
import { authCacheManager } from "@features/auth/compat/auth-cache-compat";
import { LoginType } from "@features/auth/compat/sdk-types";
import { getItem } from "utils";
import { hasJustLoggedOutFlag } from "@features/login/authSessionSyncRefs";
import { LoginAuthInfo, LoginCompleteParams, LoginState } from "../types";
import { clearBusinessStateOnLogout } from "./logoutCleanup";
import { UserStore } from "./user";

export { clearJustLoggedOutFlag } from "@features/login/authSessionSyncRefs";

const ospSDKAuthMap = {
  dev: "osp.https://api.dev.ospprotocol.xyz/v2.credentials",
  pre: "osp.https://api.opensocial.fun/v2.credentials",
  beta: "osp.https://api.beta.ospprotocol.xyz/v2.credentials",
  prod: "osp.https://api.edwealth.ai/v2.credentials",
};

function getOspSdkCredentialsKey(): string | undefined {
  const env = process.env.NEXT_PUBLIC_ENV;
  return ospSDKAuthMap[env as keyof typeof ospSDKAuthMap];
}

function checkOspSDKAuth() {
  const ospSDKAuthKey = getOspSdkCredentialsKey();
  if (!ospSDKAuthKey) {
    return false;
  }
  const auth = localStorage.getItem(ospSDKAuthKey);
  return !!auth;
}

/** 清除 OSP SDK localStorage credentials（冷启动兜底用） */
function clearOspSdkCredentials(): void {
  const ospSDKAuthKey = getOspSdkCredentialsKey();
  if (!ospSDKAuthKey) {
    return;
  }
  try {
    localStorage.removeItem(ospSDKAuthKey);
  } catch {
    // ignore
  }
}

/**
 * 冷启动发现无有效凭证时的兜底清理。
 *
 * 只清"上一个会话残留"，不写登出标记 —— 这不是一次登出。
 * 用户主动登出统一走 `AuthOrchestrator.logout()`，不再经过本 store。
 */
function clearStaleSessionState(): void {
  clearOspSdkCredentials();
  clearBusinessStateOnLogout();
  LoginStore.eoaAddress = undefined;
  LoginStore.isLoggedIn = false;
  LoginStore.isFetching = false;
  LoginStore.loginType = undefined;
  LoginStore.subLoginType = undefined;
  LoginStore.auth = undefined;
}

export const LoginStore = proxy<LoginState>({
  isLoading: true,
  isSocialLoginIntent: false,
  isLoggedIn: false,
  isFetching: true,
  updateAuthInfo: (auth: LoginAuthInfo) => {
    LoginStore.auth = auth;
  },
  loginComplete: (params: LoginCompleteParams) => {
    const { loginType, eoaAddress, auth, subLoginType } = params;

    // 增量更新
    if (eoaAddress && eoaAddress !== LoginStore.eoaAddress) {
      LoginStore.eoaAddress = eoaAddress;
      UserStore.eoaAddress = eoaAddress;
    }
    if (loginType !== undefined && loginType !== LoginStore.loginType) {
      LoginStore.loginType = loginType;
    }
    if (
      subLoginType !== undefined &&
      subLoginType !== LoginStore.subLoginType
    ) {
      LoginStore.subLoginType = subLoginType;
    }
    if (auth && auth !== LoginStore.auth) {
      LoginStore.auth = auth;
      if (auth.accessToken !== UserStore.accessToken) {
        UserStore.accessToken = auth.accessToken;
      }
    }
    if (!LoginStore.isLoggedIn) {
      LoginStore.isLoggedIn = true;
    }
    LoginStore.isLoading = false;
    LoginStore.isFetching = false;

    Track.log("info", "login", "[trace] loginComplete", {
      loginType: LoginStore.loginType,
      eoaAddress: LoginStore.eoaAddress,
      auth: String(LoginStore.auth),
      subLoginType,
    });
    // 运行时 require，避免 login.ts 顶层静态 import shellUserProfileNativeSync 扰动模块初始化顺序（SSR useSnapshot）
    //
    // 必须延后到微任务：initFromCache() 在本模块 module evaluation 期间就会调用 loginComplete，
    // 若此处同步执行，syncShellUserProfileToNativeIfInApp() 内部 require 回本模块时本模块尚未求值完成，
    // 拿到 undefined 直接抛 TypeError —— App 内已登录冷启动会整页落到 global-error。
    queueMicrotask(() => {
      try {
        require("@/src/lib/bridge/shellUserProfileNativeSync").syncShellUserProfileToNativeIfInApp();
      } catch (error) {
        // 展示用快照同步失败不应影响登录本身
        Track.log("warn", "login", "sync shell user profile to native failed", {
          cause: String(error),
        });
      }
    });
  },
});

// 使用 subscribeKey 监听 LoginStore 的 eoaAddress
subscribeKey(LoginStore, "eoaAddress", (value) => {
  if (value) {
    UserStore.eoaAddress = value;
    UserStore.fetchUserInfo(value);
  } else {
  }
});

// 使用 subscribeKey 监听 LoginStore 的 isLoggedIn
subscribeKey(LoginStore, "isLoggedIn", (value) => {
  if (value) {
    // 无 eoaAddress 时仍拉用户信息（社交登录仅有 Firebase uid）
    if (!LoginStore.eoaAddress) {
      UserStore.fetchUserInfo("", true);
    }
  }
});

// 🔑 激进策略：模块初始化时从 localStorage 检查缓存并恢复状态
(function initFromCache() {
  // SSR 环境下不执行
  if (typeof window === "undefined") {
    LoginStore.isFetching = false;
    return;
  }

  if (hasJustLoggedOutFlag()) {
    clearOspSdkCredentials();
    LoginStore.isFetching = false;
    return;
  }

  const ospLoginCacheEarly = authCacheManager.getOspLoginCache();
  if (!ospLoginCacheEarly?.accessToken && checkOspSDKAuth()) {
    clearOspSdkCredentials();
  }

  // SDK credentials 缺失时：若有 OSP 登录缓存则保留，由 OspLoginProvider 回填 SDK storage
  if (!checkOspSDKAuth()) {
    const ospLoginCache = authCacheManager.getOspLoginCache();
    if (!ospLoginCache?.accessToken) {
      LoginStore.isFetching = false;
      // 兜底：token 已失效但 UserStore / favoritesCenter 可能仍留着上一个用户的数据
      clearStaleSessionState();
      return;
    }
  }

  const ownerInfoCache = authCacheManager.getOwnerInfoCache(
    getItem("_user_id") || undefined,
  );

  if (!ownerInfoCache?.ownerInfo) {
    LoginStore.isFetching = false;
    return;
  }

  const ospLoginCache = authCacheManager.getOspLoginCache();
  if (!ospLoginCache?.accessToken) {
    LoginStore.isFetching = false;
    return;
  }

  // 如果缓存正在刷新 accessToken，则先不进行登录流程，等待刷新完成后再进行，同时isFetching为true，保证页面加载还是显示loading状态或则进行中状态
  if (ospLoginCache.isRefreshAccessToken) {
    LoginStore.isFetching = true;
    return;
  }

  // 恢复 UserStore 数据
  UserStore.accessToken = ospLoginCache.accessToken;
  UserStore.fetchUserInfoFromCache();

  LoginStore.loginComplete({
    loginType: ospLoginCache.loginType ?? (undefined as unknown as LoginType),
    eoaAddress:
      ospLoginCache.loginInfo?.eoaAddress ||
      ownerInfoCache.ownerInfo?.eoaAddress ||
      null,
    auth: {
      accessToken: ospLoginCache.accessToken,
      refreshToken: ospLoginCache.refreshToken || "",
      idToken: ospLoginCache.idToken,
    },
  });
})();
